import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { PageHeader, Button, showSnackbar } from "@cofano/cck";
import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { DuplicateCharterDocument } from "__generated__/view/charters/graphql/charter.mutations.generated";
import Helmet from "components/helmet/Helmet";
import { DOSSIER_QUERY } from "view/charters/utils/dossierutils";
import { Language } from "imports/Utils";
import { PurchaseCharter, SalesCharter, Voyage } from "types/index";

import useSalesCharterBlock from "./dossier/salescharter/useSalesCharterBlock";
import useCargoBlock from "./dossier/cargo/useCargoBlock";
import useShipBlock from "./dossier/ship/useShipBlock";
import useContractBlock from "./dossier/contract/useContractBlock";
import ParentContractCharterAlert from "./components/ParentContractCharterAlert";

import "./styling/styling.scss";
import DemurrageTab from "./sections/DemurrageTab";
import VoyageInfo from "view/voyages/components/details/VoyageInfo";
import useTabs from "components/hooks/useTabs";
import CharterDocuments from "../charters/sections/CharterDocument";
import { getMultiValuesField } from "components/utils/Permission";
import { useStoreState } from "ezStore";
import Grid from "@mui/material/Grid2";
import { SalesCharterSuggestedContractsDocument } from "__generated__/view/charters/graphql/charter.queries.generated";
import { useConfirm } from "components/hooks/useConfirm";
import useLinkContract from "view/contractsNew/utils/useLinkContract";
import { FormattedDateWindow } from "utils/DateUtils";
import { VoyageDetailsDocument } from "__generated__/view/voyages/graphql/voyage.queries.generated";

const salesCharterTabs = {
	DETAILS: "details",
	PRICING: "pricing",
	CONTACTS: "contacts",
	VOYAGE: "voyage",
	LAYTIME: "laytime",
	DOCUMENTS: "documents",
	INVOICING: "invoicing",
};

const SalesCharterDetails = () => {

	const params = useParams();
	const navigate = useNavigate();
	const confirm = useConfirm();

	const [ dossier, setDossier ] = useState<{ charter: SalesCharter | undefined, otherCharter: PurchaseCharter | undefined }>({
		charter: undefined,
		otherCharter: undefined,
	});

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: salesCharterTabs.DETAILS,
		param: "sales-charter-tab",
	});

	const warnIncompatibleContract = (charter: SalesCharter | undefined) => {
		if (!charter || !("hasApplicableContract" in charter) || charter.hasApplicableContract) {
			return;
		}

		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "INCOMPATIBLE_CONTRACT_WARNING_TITLE" }),
			content: <div>{intl.formatMessage({ id: "INCOMPATIBLE_CONTRACT_WARNING" })}</div>,
			confirmText: intl.formatMessage({ id: "OK" }),
			hideCancel: true,
		});
	};

	const [ getDossier, { refetch, loading: dossierLoading } ] = useLazyQuery(DOSSIER_QUERY, {
		onCompleted: (response) => {
			setDossier({
				charter: response.dossier.charter as SalesCharter,
				otherCharter: response.dossier.charters[0] as PurchaseCharter,
			});

			warnIncompatibleContract(response.dossier.charter as SalesCharter);
		},
	});

	const [ duplicateCharter, { loading: duplicateLoading } ] = useMutation(DuplicateCharterDocument);

	const [ getSuggestedContracts, { loading: suggestedContractsLoading } ] = useLazyQuery(SalesCharterSuggestedContractsDocument);
	const { linkContract, loading: linkContractLoading } = useLinkContract({
		onLink: () => refetch(),
	});

	const loading = !!(dossierLoading || (params.id && !dossier.charter));

	const voyage = (dossier.otherCharter?.voyages?.[0] || dossier.charter?.voyages?.[0] || undefined) as Voyage | undefined;

	const { salesCharterBlock, salesCharterInfoBlock, salesCharterContactsBlock } = useSalesCharterBlock({
		charter: dossier.charter,
		voyage: voyage,
		loading: loading,
		refetch: refetch,
		onAdd: (charterId: number) => {
			navigate(`/view/salescharters/${charterId}/details`);
		},
		onDelete: () => {
			navigate("/view/salescharters/create");

			setDossier({
				charter: undefined,
				otherCharter: undefined,
			});
		},
	});

	const autoLinkContract = (charter: SalesCharter) => {
		if (charter?.contract) {
			return;
		}

		const locations = charter?.cargos
			?.flatMap(c => c.actions)
			.map(a => a.location?.id)
			.filter((id): id is number => id !== null && id !== undefined) ?? [];

		if (charter && locations.length > 0) {
			getSuggestedContracts({
				variables: {
					charterId: charter.id,
					pagination: {
						size: 2,
						page: 0,
					},
				},
			}).then((response) => {

				const contracts = response.data?.suggestedContracts?.content ?? [];

				if (contracts.length === 1) {

					const contract = contracts[0];

					confirm({
						type: "WARNING",
						title: intl.formatMessage({ id: "AUTO_LINK_CONTRACT" }),
						content: (
							<div>
								<b>{intl.formatMessage({ id: "CONTRACT_NUMBER" })}</b>: {contract.contractNumber}<br />
								<b>{intl.formatMessage({ id: "VALID_BETWEEN" })}</b>: <FormattedDateWindow from={contract.validFrom} to={contract.validUntil} /> <br />
								<b>{intl.formatMessage({ id: "DESCRIPTION" }) }</b>: {contract.description}
							</div>
						),
					}).then(() => {
						linkContract(contract, charter);
					});
				}
			});
		}
	};

	const { cargoBlock } = useCargoBlock({
		charter: dossier.charter,
		loading: loading,
		refetch: () => refetch().then(result => {
			autoLinkContract(result.data?.dossier?.charter as SalesCharter);
		}),
	});

	const { shipBlock } = useShipBlock({
		charter: dossier.charter,
		voyage: voyage,
		loading: loading,
		resetShipNomination: true,
		refetch: () => refetch(),
	});

	const { contractBlock, agreementsBlock, invoicingBlock } = useContractBlock({
		charter: dossier.charter,
		contract: dossier.charter?.contract || dossier.charter?.allContracts?.[0],
		loading: loading || suggestedContractsLoading || linkContractLoading,
		refetch: () => refetch(),
		isOtherCharter: false,
		otherContractId: dossier.otherCharter?.contract?.id,
		voyageId: voyage?.id,
	});

	const { contractBlock: otherContractBlock, agreementsBlock: otherAgreementsBlock, invoicingBlock: otherInvoicingBlock } = useContractBlock({
		charter: dossier.otherCharter,
		contract: dossier.otherCharter?.contract || dossier.otherCharter?.allContracts?.[0],
		loading: loading,
		refetch: () => refetch(),
		isOtherCharter: true,
		otherContractId: dossier.charter?.contract?.id,
		voyageId: voyage?.id,
	});

	useEffect(() => {
		if (params.id) {
			getDossier({
				variables: {
					id: Number(params.id),
				},
			}).then(result => {
				autoLinkContract(result.data?.dossier?.charter as SalesCharter);
			});
		}
	}, [ params.id ]);

	const session = useStoreState(store => store.session);

	const customAgreements = getMultiValuesField(session, "customAgreementTypes").split(";").filter(s => s.length > 0);
	const useDemurrageTab = customAgreements.length == 0 || customAgreements.includes("DEMURRAGE_CALCULATION");
	const isParentCharter = dossier.charter?.contract?.isParentContract === true;

	return (
		<div className="sales-charter-details">
			<PageHeader>
				<PageHeader.Title>
					{Language("SALES_CHARTER")}
					<Helmet title={intl.formatMessage({ id: "SALES_CHARTER" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button
						onClick={() => navigate("/view/salescharters")}
						variant="outlined"
					>
						{Language("SALES_CHARTERS")}
					</Button>

					{dossier.charter && (
						<Button
							icon={<ContentCopyIcon fontSize="small" />}
							variant="outlined"
							loading={duplicateLoading}
							onClick={() => {
								if (!dossier.charter) {
									return;
								}
								duplicateCharter({
									variables: { charterId: dossier.charter.id },
								}).then((result) => {
									showSnackbar(intl.formatMessage({ id: "DUPLICATE_CHARTER_SUCCESS" }), { variant: "success" });
									const newId = result.data?.duplicateCharter?.id;
									if (newId) {
										navigate(`/view/salescharters/${newId}/details`);
									}
								});
							}}
						/>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{salesCharterInfoBlock}

			<div>
				<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 3 }}>
					<Tabs value={selectedTab} onChange={(_, newValue) => selectTab(newValue)}>
						<Tab label={Language("DETAILS")} value={salesCharterTabs.DETAILS} disabled={!dossier.charter} />
						<Tab label={Language("PRICING")} value={salesCharterTabs.PRICING} disabled={!dossier.charter?.contract && !dossier.otherCharter?.contract} />
						<Tab label={Language("CONTACTS")} value={salesCharterTabs.CONTACTS} disabled={!dossier.charter} />
						<Tab label={Language("VOYAGE")} value={salesCharterTabs.VOYAGE} disabled={!dossier.charter || !voyage} />
						{useDemurrageTab && (
							<Tab label={Language("CONTRACT_DEMURRAGE")} value={salesCharterTabs.LAYTIME} disabled={!dossier.charter || !voyage} />
						)}
						<Tab label={Language("DOCUMENTS")} value={salesCharterTabs.DOCUMENTS} disabled={!dossier.charter} />
						<Tab label={Language("INVOICING")} value={salesCharterTabs.INVOICING} disabled={!dossier.charter?.contract && !dossier.otherCharter?.contract} />
					</Tabs>
				</Box>
			</div>

			{selectedTab === salesCharterTabs.DETAILS && (
				<Grid container spacing={2}>
					{isParentCharter && (
						<Grid size={12}><ParentContractCharterAlert /></Grid>
					)}
					{!isParentCharter && (
						<Grid size={12}>{cargoBlock}</Grid>
					)}
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>{salesCharterBlock}</Grid>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>{shipBlock}</Grid>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>{contractBlock}</Grid>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>{otherContractBlock}</Grid>
				</Grid>
			)}

			{selectedTab === salesCharterTabs.PRICING && dossier.charter && (
				<Grid container spacing={2}>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>
						{agreementsBlock}
					</Grid>

					<Grid size={{ lg: 6, md: 6, xs: 12 }}>
						{otherAgreementsBlock}
					</Grid>
				</Grid>
			)}

			{selectedTab === salesCharterTabs.CONTACTS && dossier.charter && (
				<Grid container spacing={3}>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>
						{salesCharterContactsBlock}
					</Grid>
				</Grid>
			)}

			{selectedTab === salesCharterTabs.VOYAGE && voyage && (
				<div>
					<VoyageTab voyageId={voyage.id} refetchCharter={refetch} />
				</div>
			)}

			{selectedTab === salesCharterTabs.LAYTIME && dossier.charter && voyage && (
				<div>
					<DemurrageTab
						charter={dossier.charter}
						voyageId={voyage.id}
						refetch={refetch}
					/>
				</div>
			)}

			{selectedTab === salesCharterTabs.DOCUMENTS && dossier.charter && (
				<div>
					<CharterDocuments charter={dossier.charter} />
				</div>
			)}

			{selectedTab === salesCharterTabs.INVOICING && (
				<Grid container spacing={2}>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>
						{invoicingBlock}
					</Grid>

					<Grid size={{ lg: 6, md: 6, xs: 12 }}>
						{otherInvoicingBlock}
					</Grid>
				</Grid>
			)}
		</div>
	);
};

const VoyageTab = ({ voyageId, refetchCharter }: { voyageId: number, refetchCharter: () => void }) => {
	const { data, refetch: refetchVoyage } = useQuery(VoyageDetailsDocument, {
		variables: { id: voyageId },
	});

	const refetch = () => {
		refetchVoyage();
		refetchCharter();
	};

	if (data?.voyage) {
		return (
			<VoyageInfo
				voyage={data.voyage}
				refetch={refetch}
				tabsQueryParameterName="voyage-tab"
			/>
		);
	} else {
		return <CircularProgress size={24} />;
	}
};

export default SalesCharterDetails;
